import React, { FC } from 'react';
import { Control, UseFormClearErrors, UseFormSetValue, useWatch } from 'react-hook-form';

import { LockIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { FormInput } from '../../../components/input/index';
import { Tooltip } from '../../../components/tooltip/index';
import { Company, useLocale } from '../../../hooks/index';
import { EventTypeBackend } from '../../../types/events.types';
import { toNumber } from '../../../utils/to-number.util';
import {
  FormDropDown,
  IssuanceRowStatus,
  ReadOnlyField,
  ReverseVestingFields,
  ShareClassCombobox,
  StakeholderCombobox,
} from '../event-form/components/index';
import {
  companyIncorporationIssuanceSchema,
  FormSchema,
  shareIssuanceItemSchema,
} from '../event-form/event-form.validation';
import {
  isReverseVestingIncomplete,
  resetReverseVestingOnStakeholderChange,
} from '../event-form/utils/reverse-vesting-form.util';
import {
  ManageReverseVestingEvent,
  manageReverseVestingRowPath,
  manageReverseVestingRows,
  manageReverseVestingSharePrice,
} from './manage-reverse-vesting.util';

export type ManageReverseVestingRowsStepProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  event: ManageReverseVestingEvent;
  /** Row the "⋮" menu was opened on — starts expanded, the rest collapsed. */
  expandedRowId?: string;
  isLocked: boolean;
  onCancel: () => void;
  onNext: () => void;
  setFormData: UseFormSetValue<FormSchema>;
};

const fieldLabelClassName = 'text-sm font-[450] text-gray-700';

/**
 * Explains why the issuance details below are frozen. Shown for an event that a newer valuation
 * has locked — the action itself stays available, so the banner has to say what is still editable.
 */
const ManageReverseVestingLockedBanner: FC = () => (
  <div className="flex w-full items-center rounded-md bg-sunbeam-50 p-3">
    <div className="flex w-full gap-2">
      <div className="relative size-6 shrink-0">
        <LockIcon
          className="absolute left-1/2 top-1/2 size-5 -translate-x-1/2 -translate-y-1/2"
          fillColor="#FEF0C7"
          iconColor="#DC6803"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 break-words text-gray-800">
        <span className="text-sm font-[550]">
          <AppFormattedMessage id="MANAGE_REVERSE_VESTING_LOCKED_TITLE" />
        </span>
        <div className="flex flex-col gap-2 text-xs font-[450]">
          <span>
            <AppFormattedMessage id="MANAGE_REVERSE_VESTING_LOCKED_DESCRIPTION" />
          </span>
          <span>
            <AppFormattedMessage id="MANAGE_REVERSE_VESTING_LOCKED_EDITABLE_HINT" />
          </span>
        </div>
      </div>
    </div>
  </div>
);

export const ManageReverseVestingRowsStep: FC<ManageReverseVestingRowsStepProps> = ({
  clearErrors,
  companyId,
  control,
  event,
  expandedRowId,
  isLocked,
  onCancel,
  onNext,
  setFormData,
}) => {
  const { messagesLocale } = useLocale();
  const { selectedCompany: { currency } = {} } = Company.useSelected();

  const isIncorporation = event.type === EventTypeBackend.COMPANY_INCORPORATION;
  const rows = manageReverseVestingRows(event);
  // Watch scoped to the rows slice this modal edits — a whole-form watch re-rendered the step for
  // form changes it can never display.
  const formRows = useWatch({
    control,
    name: isIncorporation
      ? 'companyIncorporation.issuances'
      : 'optionalStep.share-issuance.issuanceItems',
  }) as NonNullable<FormSchema['companyIncorporation']>['issuances'] | undefined;

  const currencySymbol = currency?.symbol ?? '$';
  const formatCurrency = (value: number) =>
    `${currencySymbol}${value.toLocaleString('en-US', { maximumFractionDigits: 7 })}`;

  /**
   * The row's own schema decides completeness rather than `formState.errors`: a row the user never
   * touched has no errors yet, so an incomplete schedule would otherwise read as ready to save.
   * Parsed once per row per render — events can carry up to 100 rows.
   */
  const rowSchema = isIncorporation ? companyIncorporationIssuanceSchema : shareIssuanceItemSchema;
  const rowCompleteness = rows.map((_, index) => rowSchema.safeParse(formRows?.[index]).success);

  const isValid = rows.length > 0 && rowCompleteness.every(Boolean);

  const getRowStatus = (index: number) => {
    const row = formRows?.[index];

    return {
      isComplete: rowCompleteness[index],
      isReverseVestingEnabled: Boolean(row?.reverseVesting?.enabled),
      isReverseVestingIncomplete: isReverseVestingIncomplete(row),
      stakeholderName: row?.stakeholder?.fullName || messagesLocale['ISSUANCE'],
    };
  };

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        {isLocked && <ManageReverseVestingLockedBanner />}

        {rows.map((row, index) => {
          const namePrefix = manageReverseVestingRowPath(event.type, index);
          // Lock freezes the issuance details; termination freezes the whole row, schedule
          // included. They are separate conditions and a row can be under both.
          const isTerminatedStakeholder = Boolean(
            formRows?.[index]?.stakeholder?.isTerminated ?? row.isTerminatedStakeholder,
          );
          const areIssuanceFieldsReadOnly = isLocked || isTerminatedStakeholder;
          // `toNumber` floors an empty field to 0 rather than returning nullish, so the
          // emptied-input case is caught before the call — a `??` after it could never fire, and
          // the row would price its consideration at zero while the user retypes.
          const sharesInput = formRows?.[index]?.shares;
          const shares = sharesInput === undefined ? row.sharesCount : toNumber(sharesInput);

          return (
            <FormDropDown
              handleRemove={() => ''}
              isOpenOnStart={expandedRowId ? row.id === expandedRowId : index === 0}
              key={row.id}
              showRemoveButton={false}
              title={`${messagesLocale['ISSUANCE']} - ${row.stakeholderName}`}
              titleSlot={<IssuanceRowStatus {...getRowStatus(index)} />}
            >
              <div className="flex w-full flex-col gap-3">
                {areIssuanceFieldsReadOnly ? (
                  // Only the lock is explained here. A terminated row is frozen for a reason that
                  // is about the schedule, and that message belongs on the toggle it applies to —
                  // repeating it over the cards too just doubles the same tooltip.
                  <Tooltip
                    childrenWrapperClassName="w-full"
                    className="w-[344px]"
                    content={messagesLocale['MANAGE_REVERSE_VESTING_LOCKED_FIELD_TOOLTIP']}
                    disabled={!isLocked}
                  >
                    <div className="grid w-full grid-cols-2 gap-3">
                      <ReadOnlyField
                        label={<AppFormattedMessage id="STAKEHOLDER" />}
                        value={row.stakeholderName}
                      />
                      <ReadOnlyField
                        label={<AppFormattedMessage id="SHARE_CLASS" />}
                        value={row.shareClassName}
                      />
                      <ReadOnlyField
                        label={<AppFormattedMessage id="NO_OF_SHARES" />}
                        value={row.sharesCount.toLocaleString('en-US')}
                      />
                      <ReadOnlyField
                        label={<AppFormattedMessage id="CONSIDERATION_PAID" />}
                        value={formatCurrency(row.considerationPaid)}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <>
                    <div className="flex w-full items-center justify-between">
                      <span className={fieldLabelClassName}>
                        <AppFormattedMessage id="STAKEHOLDER" />
                      </span>
                      <StakeholderCombobox
                        companyId={companyId}
                        control={control}
                        name={`${namePrefix}.stakeholder`}
                        onSelectStakeholder={({ id }) =>
                          resetReverseVestingOnStakeholderChange({
                            clearErrors,
                            currentStakeholderId: formRows?.[index]?.stakeholder?.id,
                            namePrefix: `${namePrefix}.reverseVesting`,
                            nextStakeholderId: id,
                            setFormData,
                          })
                        }
                      />
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <span className={fieldLabelClassName}>
                        <AppFormattedMessage id="SHARE_CLASS" />
                      </span>
                      <ShareClassCombobox
                        companyId={companyId}
                        control={control}
                        fieldName={`${namePrefix}.shareClass`}
                      />
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <span className={fieldLabelClassName}>
                        <AppFormattedMessage id="NO_OF_SHARES" />
                      </span>
                      <FormInput
                        control={control}
                        name={`${namePrefix}.shares`}
                        numberOnly
                        placeholder={<AppFormattedMessage id="VALUE" />}
                        shouldFormatNumber
                        wrapperClassName="w-[224px]"
                      />
                    </div>
                    <div className="flex w-full items-center justify-between">
                      <span className={fieldLabelClassName}>
                        <AppFormattedMessage id="CONSIDERATION_PAID" />
                      </span>
                      <span className="rounded-md bg-brand-25 px-3 py-1 text-sm font-[550] text-brand-700">
                        {formatCurrency(shares * manageReverseVestingSharePrice(event))}
                      </span>
                    </div>
                  </>
                )}

                <ReverseVestingFields
                  clearErrors={clearErrors}
                  control={control}
                  disabled={isTerminatedStakeholder}
                  disabledReason={messagesLocale['REVERSE_VESTING_TERMINATED_STAKEHOLDER']}
                  issuedShares={shares}
                  namePrefix={`${namePrefix}.reverseVesting`}
                  setFormData={setFormData}
                />
              </div>
            </FormDropDown>
          );
        })}
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <Button
          className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
          disabled={!isValid}
          onClick={onNext}
          type="button"
        >
          <AppFormattedMessage id="NEXT" />
        </Button>
      </div>
    </>
  );
};
